import { z } from 'zod';

/**
 * Reading an OpenAI-compatible streamed chat completion (`stream: true`): server-sent events whose
 * `data:` lines carry JSON chunks, then `data: [DONE]`.
 */

/**
 * Splits a text stream into `data:` payloads. OpenAI-compatible servers (LM Studio, Ollama,
 * llama.cpp, vLLM) put each JSON chunk on one line, so each `data:` line is one payload; that also
 * copes with servers that skip the blank line between events. Other fields and comments are dropped.
 */
export function createSseParser() {
  let buffer = '';
  const payload = (line: string): string[] => {
    if (!line.startsWith('data:')) return [];
    const value = line.slice(5);
    return [value.startsWith(' ') ? value.slice(1) : value];
  };
  return {
    push(text: string): string[] {
      buffer += text;
      const lines = buffer.split(/\r\n|\n|\r/);
      buffer = lines.pop() ?? '';
      return lines.flatMap(payload);
    },
    /** The last line, when the stream ends without a newline. */
    end(): string[] {
      const rest = buffer;
      buffer = '';
      return payload(rest);
    },
  };
}

const chunkSchema = z.object({
  choices: z
    .array(
      z.object({
        delta: z
          .object({
            content: z.string().nullish(),
            // LM Studio's name for the reasoning channel; Ollama and others use `reasoning`.
            reasoning_content: z.string().nullish(),
            reasoning: z.string().nullish(),
          })
          .nullish(),
        finish_reason: z.string().nullish(),
      }),
    )
    .nullish(),
  error: z.union([z.string(), z.object({ message: z.string().nullish() })]).nullish(),
});

export interface StreamChunk {
  /** Answer text in this chunk (reasoning never lands here). */
  content: string;
  /** The model is reasoning on its separate channel: shown as "Thinking…", never as text. */
  reasoning: boolean;
  finishReason: string | null;
  done: boolean;
  /** The server reported an error mid-stream. */
  error: string | null;
}

const EMPTY: StreamChunk = { content: '', reasoning: false, finishReason: null, done: false, error: null };

/** One `data:` payload. A line that isn't JSON is skipped rather than failing the answer. */
export function readChunk(payload: string): StreamChunk {
  if (payload.trim() === '[DONE]') return { ...EMPTY, done: true };
  let json: unknown;
  try {
    json = JSON.parse(payload);
  } catch {
    return EMPTY;
  }
  const parsed = chunkSchema.safeParse(json);
  if (!parsed.success) return EMPTY;
  const { choices, error } = parsed.data;
  if (error) {
    const message = typeof error === 'string' ? error : error.message;
    return { ...EMPTY, error: message?.trim() || 'The model server reported an error.' };
  }
  const choice = choices?.[0];
  const delta = choice?.delta;
  return {
    content: delta?.content ?? '',
    reasoning: Boolean(delta?.reasoning_content || delta?.reasoning),
    finishReason: choice?.finish_reason ?? null,
    done: false,
    error: null,
  };
}

const OPEN = '<think>';
const CLOSE = '</think>';

/** A trailing `<`, `<th`, `</thin`… that may become a tag with the next chunk. */
function partialTagAt(text: string): number {
  const lt = text.lastIndexOf('<');
  if (lt === -1) return -1;
  const tail = text.slice(lt);
  return tail.length < CLOSE.length && (OPEN.startsWith(tail) || CLOSE.startsWith(tail)) ? lt : -1;
}

/**
 * The visible answer in the raw streamed text: `<think>…</think>` blocks removed, including one
 * still streaming. Some chat templates open the block themselves, so the stream starts with the
 * reasoning and only its `</think>` arrives: everything before a close without an open is dropped.
 * Pure over the accumulated text, so it doesn't matter where the chunks split.
 */
export function stripThink(raw: string): { text: string; thinking: boolean } {
  let rest = raw;
  const firstClose = rest.indexOf(CLOSE);
  const firstOpen = rest.indexOf(OPEN);
  if (firstClose !== -1 && (firstOpen === -1 || firstClose < firstOpen)) {
    rest = rest.slice(firstClose + CLOSE.length);
  }
  let text = '';
  let thinking = false;
  for (;;) {
    const open = rest.indexOf(OPEN);
    if (open === -1) {
      text += rest;
      break;
    }
    text += rest.slice(0, open);
    const close = rest.indexOf(CLOSE, open + OPEN.length);
    if (close === -1) {
      thinking = true;
      break;
    }
    rest = rest.slice(close + CLOSE.length);
  }
  if (!thinking) {
    const partial = partialTagAt(text);
    if (partial !== -1) text = text.slice(0, partial);
  }
  return { text: text.replace(/^\s+/, ''), thinking };
}
